import React, { useState, useEffect } from 'react';
import { SERVER_URL } from '../config/api';

const ADMIN_TOKEN_KEY = 'metalbox_admin_token';

const ExhibitionManagement = () => {
    const [exhibitions, setExhibitions] = useState([]);
    const [currentExhibition, setCurrentExhibition] = useState(null);
    const [showEditor, setShowEditor] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [toast, setToast] = useState(null);
    const [confirmDialog, setConfirmDialog] = useState(null);
    const [uploadingImage, setUploadingImage] = useState(null);

    // 获取完整图片URL的辅助函数
    const getImageUrl = (imagePath) => {
        if (!imagePath) return '';
        if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
            return imagePath;
        }
        return `${SERVER_URL}${imagePath.startsWith('/') ? '' : '/'}${imagePath}`;
    };

    // 参展信息模板
    const exhibitionTemplate = {
        title_en: '',
        title_zh: '',
        description_en: '',
        description_zh: '',
        images: [],
        display_order: 0,
        is_published: true,
    };

    // 获取参展信息列表
    useEffect(() => {
        fetchExhibitions();
    }, []);

    const fetchExhibitions = async () => {
        setIsLoading(true);
        try {
            const response = await fetch(`${SERVER_URL}/api/admin/exhibitions`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem(ADMIN_TOKEN_KEY)}`,
                },
            });

            if (response.status === 401 || response.status === 403) {
                localStorage.removeItem(ADMIN_TOKEN_KEY);
                showToast('登录已过期，请重新登录', 'error');
                setTimeout(() => {
                    window.location.reload();
                }, 1500);
                return;
            }

            if (response.ok) {
                const data = await response.json();
                setExhibitions(data.exhibitions || []);
            } else {
                showToast('获取参展信息列表失败', 'error');
            }
        } catch (error) {
            console.error('获取参展信息列表失败:', error);
            showToast('获取参展信息列表失败', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    // 显示Toast
    const showToast = (message, type = 'success') => {
        setToast({ message, type });
    };

    // 创建新参展信息
    const handleCreateExhibition = () => {
        setCurrentExhibition({ ...exhibitionTemplate });
        setShowEditor(true);
    };

    // 编辑参展信息
    const handleEditExhibition = (exhibition) => {
        setCurrentExhibition({ ...exhibition });
        setShowEditor(true);
    };

    // 切换发布状态
    const handleTogglePublish = async (exhibition) => {
        try {
            const response = await fetch(
                `${SERVER_URL}/api/admin/exhibitions/${exhibition.id}/publish`,
                {
                    method: 'PUT',
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem(ADMIN_TOKEN_KEY)}`,
                    },
                }
            );
            if (response.ok) {
                const data = await response.json();
                showToast(data.message, 'success');
                fetchExhibitions();
            }
        } catch (error) {
            showToast('操作失败', 'error');
        }
    };

    // 删除参展信息
    const handleDeleteExhibition = (exhibitionId) => {
        setConfirmDialog({
            message: '确定要删除这条参展信息吗？此操作不可撤销。',
            type: 'danger',
            onConfirm: async () => {
                try {
                    const response = await fetch(
                        `${SERVER_URL}/api/admin/exhibitions/${exhibitionId}`,
                        {
                            method: 'DELETE',
                            headers: {
                                Authorization: `Bearer ${localStorage.getItem(ADMIN_TOKEN_KEY)}`,
                            },
                        }
                    );
                    if (response.ok) {
                        showToast('参展信息已删除', 'success');
                        fetchExhibitions();
                    }
                } catch (error) {
                    showToast('删除失败', 'error');
                }
                setConfirmDialog(null);
            },
            onCancel: () => setConfirmDialog(null),
        });
    };

    // 上传图片
    const handleImageUpload = async (e, index) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            showToast('请选择图片文件', 'error');
            return;
        }

        if (file.size > 30 * 1024 * 1024) {
            showToast('图片大小不能超过30MB', 'error');
            return;
        }

        const formData = new FormData();
        formData.append('image', file);

        try {
            setUploadingImage(index);
            const response = await fetch(
                `${SERVER_URL}/api/admin/exhibitions/upload-image`,
                {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem(ADMIN_TOKEN_KEY)}`,
                    },
                    body: formData,
                }
            );

            const data = await response.json();

            if (data.success) {
                // 更新图片数组
                const newImages = [...(currentExhibition.images || [])];
                newImages[index] = data.imagePath;

                setCurrentExhibition((prev) => ({
                    ...prev,
                    images: newImages,
                    // 添加时间戳强制重新渲染
                    _imageUpdateTimestamp: Date.now(),
                }));
                showToast('图片上传成功', 'success');
            } else {
                showToast(data.message || '上传失败', 'error');
            }
        } catch (error) {
            console.error('Upload error:', error);
            showToast('上传失败', 'error');
        } finally {
            setUploadingImage(null);
            e.target.value = '';
        }
    };

    // 保存参展信息
    const handleSaveExhibition = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const url = currentExhibition.id
                ? `${SERVER_URL}/api/admin/exhibitions/${currentExhibition.id}`
                : `${SERVER_URL}/api/admin/exhibitions`;

            const method = currentExhibition.id ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem(ADMIN_TOKEN_KEY)}`,
                },
                body: JSON.stringify(currentExhibition),
            });

            if (response.ok) {
                showToast(
                    currentExhibition.id ? '参展信息已更新' : '参展信息已创建',
                    'success'
                );
                setShowEditor(false);
                fetchExhibitions();
            } else {
                const data = await response.json();
                showToast(data.message || '保存失败', 'error');
            }
        } catch (error) {
            console.error('保存失败:', error);
            showToast('保存失败', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    // Toast自动消失
    useEffect(() => {
        if (toast) {
            const timer = setTimeout(() => {
                setToast(null);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [toast]);

    return (
        <div className="p-6">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold text-white">参展信息管理</h1>
                <button
                    onClick={handleCreateExhibition}
                    className="px-6 py-3 bg-[#4a5f3a] hover:bg-[#5a6f4a] text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        fill="currentColor"
                        viewBox="0 0 256 256"
                    >
                        <path d="M224,128a8,8,0,0,1-8,8H136v80a8,8,0,0,1-16,0V136H40a8,8,0,0,1,0-16h80V40a8,8,0,0,1,16,0v80h80A8,8,0,0,1,224,128Z"></path>
                    </svg>
                    创建新参展信息
                </button>
            </div>

            {/* 参展信息列表 */}
            <div className="bg-[#1a1d17] border border-[#2a2d27] rounded-lg overflow-hidden">
                {isLoading && !showEditor ? (
                    <div className="p-8 text-center text-gray-400">
                        <div className="inline-block">
                            <div className="w-8 h-8 border-2 border-gray-400 border-t-white rounded-full animate-spin"></div>
                        </div>
                        <p className="mt-2">加载中...</p>
                    </div>
                ) : exhibitions.length === 0 ? (
                    <div className="p-8 text-center text-gray-400">
                        暂无参展信息,点击上方按钮创建第一条参展信息
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-[#2a2d27]">
                                    <th className="text-left p-4 text-gray-400 font-medium">
                                        预览
                                    </th>
                                    <th className="text-left p-4 text-gray-400 font-medium">
                                        中文标题
                                    </th>
                                    <th className="text-left p-4 text-gray-400 font-medium">
                                        英文标题
                                    </th>
                                    <th className="text-left p-4 text-gray-400 font-medium">
                                        排序
                                    </th>
                                    <th className="text-left p-4 text-gray-400 font-medium">
                                        状态
                                    </th>
                                    <th className="text-left p-4 text-gray-400 font-medium">
                                        操作
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {exhibitions.map((exhibition) => (
                                    <tr
                                        key={exhibition.id}
                                        className="border-b border-[#2a2d27] hover:bg-[#262a22] transition-colors"
                                    >
                                        <td className="p-4">
                                            {exhibition.images && exhibition.images.length > 0 ? (
                                                <img
                                                    src={getImageUrl(exhibition.images[0])}
                                                    alt={exhibition.title_zh || exhibition.title_en}
                                                    className="w-20 h-20 object-cover rounded"
                                                />
                                            ) : (
                                                <div className="w-20 h-20 bg-[#2a2d27] rounded flex items-center justify-center text-gray-500 text-xs">
                                                    无图片
                                                </div>
                                            )}
                                        </td>
                                        <td className="p-4 text-white font-medium">
                                            {exhibition.title_zh || '-'}
                                        </td>
                                        <td className="p-4 text-gray-400">
                                            {exhibition.title_en || '-'}
                                        </td>
                                        <td className="p-4 text-gray-400">
                                            {exhibition.display_order}
                                        </td>
                                        <td className="p-4">
                                            <span
                                                className={`px-2 py-1 rounded text-xs border ${exhibition.is_published
                                                    ? 'bg-green-500/20 text-green-400 border-green-500/50'
                                                    : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50'
                                                    }`}
                                            >
                                                {exhibition.is_published ? '已发布' : '草稿'}
                                            </span>
                                        </td>
                                        <td className="p-4">
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => handleEditExhibition(exhibition)}
                                                    className="px-3 py-1 bg-[#4a5f3a] hover:bg-[#5a7f4a] text-white rounded text-sm transition-colors"
                                                >
                                                    编辑
                                                </button>
                                                <button
                                                    onClick={() => handleTogglePublish(exhibition)}
                                                    className={`px-3 py-1 rounded text-sm transition-colors ${exhibition.is_published
                                                        ? 'bg-orange-500/20 hover:bg-orange-500/30 text-orange-400'
                                                        : 'bg-green-500/20 hover:bg-green-500/30 text-green-400'
                                                        }`}
                                                >
                                                    {exhibition.is_published ? '下架' : '发布'}
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteExhibition(exhibition.id)}
                                                    className="px-3 py-1 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded text-sm transition-colors"
                                                >
                                                    删除
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* 编辑器弹窗 */}
            {showEditor && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4 overflow-y-auto">
                    <div className="bg-[#1a1d17] rounded-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
                        {/* 标题栏 */}
                        <div className="flex items-center justify-between p-6 border-b border-[#2a2d27]">
                            <h2 className="text-2xl font-bold text-white">
                                {currentExhibition.id ? '编辑参展信息' : '创建新参展信息'}
                            </h2>
                            <button
                                onClick={() => setShowEditor(false)}
                                className="text-gray-400 hover:text-white transition-colors"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    fill="currentColor"
                                    viewBox="0 0 256 256"
                                >
                                    <path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"></path>
                                </svg>
                            </button>
                        </div>

                        <form
                            onSubmit={handleSaveExhibition}
                            className="p-6 space-y-6 overflow-y-auto"
                        >
                            {/* 标题 */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-2">
                                        中文标题
                                    </label>
                                    <input
                                        type="text"
                                        value={currentExhibition.title_zh || ''}
                                        onChange={(e) =>
                                            setCurrentExhibition({
                                                ...currentExhibition,
                                                title_zh: e.target.value,
                                            })
                                        }
                                        className="w-full px-4 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623]"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-2">
                                        English Title
                                    </label>
                                    <input
                                        type="text"
                                        value={currentExhibition.title_en || ''}
                                        onChange={(e) =>
                                            setCurrentExhibition({
                                                ...currentExhibition,
                                                title_en: e.target.value,
                                            })
                                        }
                                        className="w-full px-4 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623]"
                                    />
                                </div>
                            </div>

                            {/* 描述 */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-2">
                                        中文描述
                                    </label>
                                    <textarea
                                        value={currentExhibition.description_zh || ''}
                                        onChange={(e) =>
                                            setCurrentExhibition({
                                                ...currentExhibition,
                                                description_zh: e.target.value,
                                            })
                                        }
                                        rows={4}
                                        className="w-full px-4 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623] resize-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-2">
                                        English Description
                                    </label>
                                    <textarea
                                        value={currentExhibition.description_en || ''}
                                        onChange={(e) =>
                                            setCurrentExhibition({
                                                ...currentExhibition,
                                                description_en: e.target.value,
                                            })
                                        }
                                        rows={4}
                                        className="w-full px-4 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623] resize-none"
                                    />
                                </div>
                            </div>

                            {/* 图片上传 */}
                            <div>
                                <label className="block text-sm font-medium text-gray-400 mb-2">
                                    参展图片（可上传多张）
                                </label>

                                {/* 现有图片列表 */}
                                <div className="space-y-3">
                                    {(currentExhibition.images || []).map((image, index) => (
                                        <div key={index} className="flex gap-2">
                                            <input
                                                type="text"
                                                value={image || ''}
                                                onChange={(e) => {
                                                    const newImages = [...(currentExhibition.images || [])];
                                                    newImages[index] = e.target.value;
                                                    setCurrentExhibition({
                                                        ...currentExhibition,
                                                        images: newImages,
                                                    });
                                                }}
                                                placeholder={`图片 ${index + 1} 路径`}
                                                className="flex-1 px-4 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623]"
                                            />
                                            <label
                                                className={`px-4 py-2 ${uploadingImage === index
                                                    ? 'bg-gray-500 cursor-not-allowed'
                                                    : 'bg-[#4a5f3a] hover:bg-[#5a6f4a] cursor-pointer'
                                                    } text-white rounded-lg transition-colors flex items-center gap-2 whitespace-nowrap`}
                                            >
                                                {uploadingImage === index ? (
                                                    <>
                                                        <svg
                                                            className="animate-spin h-5 w-5"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <circle
                                                                className="opacity-25"
                                                                cx="12"
                                                                cy="12"
                                                                r="10"
                                                                stroke="currentColor"
                                                                strokeWidth="4"
                                                            ></circle>
                                                            <path
                                                                className="opacity-75"
                                                                fill="currentColor"
                                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                            ></path>
                                                        </svg>
                                                        上传中...
                                                    </>
                                                ) : (
                                                    <>
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            width="20"
                                                            height="20"
                                                            fill="currentColor"
                                                            viewBox="0 0 256 256"
                                                        >
                                                            <path d="M216,40H40A16,16,0,0,0,24,56V200a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A16,16,0,0,0,216,40Zm0,16V158.75l-26.07-26.06a16,16,0,0,0-22.63,0l-20,20-44-44a16,16,0,0,0-22.62,0L40,149.37V56ZM40,172l52-52,80,80H40Zm176,28H194.63l-36-36,20-20L216,181.38V200ZM144,100a12,12,0,1,1,12,12A12,12,0,0,1,144,100Z"></path>
                                                        </svg>
                                                        上传
                                                    </>
                                                )}
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={(e) => handleImageUpload(e, index)}
                                                    className="hidden"
                                                    disabled={uploadingImage === index}
                                                />
                                            </label>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const newImages = currentExhibition.images.filter(
                                                        (_, i) => i !== index
                                                    );
                                                    setCurrentExhibition({
                                                        ...currentExhibition,
                                                        images: newImages,
                                                    });
                                                }}
                                                className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors"
                                            >
                                                删除
                                            </button>
                                        </div>
                                    ))}
                                </div>

                                {/* 添加图片按钮 */}
                                <button
                                    type="button"
                                    onClick={() => {
                                        setCurrentExhibition({
                                            ...currentExhibition,
                                            images: [...(currentExhibition.images || []), ''],
                                        });
                                    }}
                                    className="mt-3 w-full px-4 py-2 bg-[#2a2d27] hover:bg-[#3a3d37] text-gray-400 rounded-lg transition-colors flex items-center justify-center gap-2"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="20"
                                        height="20"
                                        fill="currentColor"
                                        viewBox="0 0 256 256"
                                    >
                                        <path d="M224,128a8,8,0,0,1-8,8H136v80a8,8,0,0,1-16,0V136H40a8,8,0,0,1,0-16h80V40a8,8,0,0,1,16,0v80h80A8,8,0,0,1,224,128Z"></path>
                                    </svg>
                                    添加图片
                                </button>

                                {/* 图片预览 */}
                                {currentExhibition.images && currentExhibition.images.length > 0 && (
                                    <div className="mt-4">
                                        <label className="block text-sm font-medium text-gray-400 mb-2">
                                            预览
                                        </label>
                                        <div className="grid grid-cols-2 gap-3">
                                            {currentExhibition.images.map((image, index) => (
                                                image && (
                                                    <div
                                                        key={index}
                                                        className="relative h-48 bg-[#1a1d17] border border-[#2a2d27] rounded-lg overflow-hidden"
                                                    >
                                                        <img
                                                            src={getImageUrl(image)}
                                                            alt={`预览 ${index + 1}`}
                                                            className="w-full h-full object-contain"
                                                            onError={(e) => {
                                                                e.target.src =
                                                                    'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23333" width="200" height="200"/%3E%3Ctext fill="%23666" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3E图片加载失败%3C/text%3E%3C/svg%3E';
                                                            }}
                                                        />
                                                    </div>
                                                )
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* 排序 */}
                            <div>
                                <label className="block text-sm font-medium text-gray-400 mb-2">
                                    显示顺序（数字越小越靠前）
                                </label>
                                <input
                                    type="number"
                                    value={currentExhibition.display_order || 0}
                                    onChange={(e) =>
                                        setCurrentExhibition({
                                            ...currentExhibition,
                                            display_order: parseInt(e.target.value) || 0,
                                        })
                                    }
                                    className="w-full px-4 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623]"
                                />
                            </div>

                            {/* 发布状态 */}
                            <div className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    id="is_published"
                                    checked={currentExhibition.is_published || false}
                                    onChange={(e) =>
                                        setCurrentExhibition({
                                            ...currentExhibition,
                                            is_published: e.target.checked,
                                        })
                                    }
                                    className="w-4 h-4 text-[#4a5f3a] bg-[#1a1d17] border-[#2a2d27] rounded focus:ring-[#4a5f3a]"
                                />
                                <label
                                    htmlFor="is_published"
                                    className="text-sm font-medium text-gray-400"
                                >
                                    立即发布
                                </label>
                            </div>

                            {/* 操作按钮 */}
                            <div className="flex gap-4 pt-4 border-t border-[#2a2d27]">
                                <button
                                    type="button"
                                    onClick={() => setShowEditor(false)}
                                    className="flex-1 px-6 py-3 bg-[#2a2d27] hover:bg-[#3a3d37] text-white rounded-lg font-medium transition-colors"
                                >
                                    取消
                                </button>
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="flex-1 px-6 py-3 bg-[#4a5f3a] hover:bg-[#5a6f4a] text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                                >
                                    {isLoading ? '保存中...' : '保存'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Toast通知 */}
            {toast && (
                <div className="fixed top-4 right-4 z-50 animate-slide-in-right">
                    <div
                        className={`px-6 py-3 rounded-lg shadow-lg ${toast.type === 'success'
                            ? 'bg-green-500 text-white'
                            : toast.type === 'error'
                                ? 'bg-red-500 text-white'
                                : 'bg-blue-500 text-white'
                            }`}
                    >
                        {toast.message}
                    </div>
                </div>
            )}

            {/* 确认对话框 */}
            {confirmDialog && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4">
                    <div className="bg-[#1a1d17] rounded-xl p-6 max-w-md w-full border border-[#2a2d27]">
                        <h3 className="text-xl font-bold text-white mb-4">确认操作</h3>
                        <p className="text-gray-400 mb-6">{confirmDialog.message}</p>
                        <div className="flex gap-4">
                            <button
                                onClick={confirmDialog.onCancel}
                                className="flex-1 px-6 py-3 bg-[#2a2d27] hover:bg-[#3a3d37] text-white rounded-lg font-medium transition-colors"
                            >
                                取消
                            </button>
                            <button
                                onClick={confirmDialog.onConfirm}
                                className={`flex-1 px-6 py-3 rounded-lg font-medium transition-colors ${confirmDialog.type === 'danger'
                                    ? 'bg-red-500 hover:bg-red-600 text-white'
                                    : 'bg-[#4a5f3a] hover:bg-[#5a6f4a] text-white'
                                    }`}
                            >
                                确认
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ExhibitionManagement;
